import { useEffect, useState } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { RealtimeChannel } from '@supabase/supabase-js'

export interface PresenceUser {
  user_id: string
  username: string
  avatar_url?: string
  online_at: string
}

/**
 * Global presence tracking hook using Supabase Realtime
 * Tracks all users online across the entire application
 *
 * @returns Object containing onlineUsers array and onlineCount number
 */
export function useGlobalPresence() {
  const [onlineUsers, setOnlineUsers] = useState<PresenceUser[]>([])
  const [onlineCount, setOnlineCount] = useState(0)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let channel: RealtimeChannel | null = null

    const setupPresence = async () => {
      try {
        // Get current user
        const { data: { user } } = await supabase.auth.getUser()

        // Create channel for global presence
        channel = supabase.channel('online-users')

        // Handle presence sync events
        channel
          .on('presence', { event: 'sync' }, () => {
            const state = channel!.presenceState()
            const users: PresenceUser[] = []

            for (const userId in state) {
              const presences = state[userId] as any[]
              if (presences.length > 0) {
                users.push(presences[0] as PresenceUser)
              }
            }

            setOnlineUsers(users)
            setOnlineCount(users.length)
            setLoading(false)
          })
          .on('presence', { event: 'join' }, ({ newPresences }) => {
            // Presence tracking active - uncomment for debugging
            // console.log('User joined:', newPresences)
          })
          .on('presence', { event: 'leave' }, ({ leftPresences }) => {
            // Presence tracking active - uncomment for debugging
            // console.log('User left:', leftPresences)
          })
          .subscribe(async (status) => {
            if (status === 'SUBSCRIBED' && user) {
              // Get user profile for display name and avatar
              const { data: profile } = await supabase
                .from('user_profiles')
                .select('display_name, username, avatar_url')
                .eq('user_id', user.id)
                .single()

              // Track current user's presence
              await channel!.track({
                user_id: user.id,
                username: profile?.display_name || profile?.username || 'Anonymous',
                avatar_url: profile?.avatar_url,
                online_at: new Date().toISOString()
              })
            } else if (status === 'SUBSCRIBED' && !user) {
              // If not authenticated, still show other users but don't track self
              setLoading(false)
            }
          })
      } catch (error) {
        console.error('Error setting up global presence:', error)
        setLoading(false)
      }
    }

    setupPresence()

    // Cleanup: unsubscribe and untrack presence
    return () => {
      if (channel) {
        channel.unsubscribe()
      }
    }
  }, [])

  return { onlineUsers, onlineCount, loading }
}
